import React, { useEffect, useState, useRef } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import moment from "moment";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

import * as PaymentLinkActions from "../../Store/Actions/PaymentLink";
import * as OrderActions from "../../Store/Actions/Order";
import { formatNumberCurrency, validarNumeroCartao, validarValidadeCartao } from "../../Components/Utils";
import { getUsdRate } from "../../Utils/exchange";
import { publicBaseUrl } from "../../Utils/publicUrl";
import { getDeadlineUrgency, PAYMENT_LINK_STATUS } from "../../Constants/paymentLink";
import { absoluteLogoUrl } from "../../Services/agencyLogo";
import { downloadReceiptPdf, fetchReceiptPdfUrl, viewReceiptPdf } from "../../Utils/receiptPdf";
import dream4Logo from "../../Assets/Images/logoHeader.svg";
import imagePlaceHolder from "../../Assets/Images/noimg.jpg";
import { onlyDigits, maskCard, maskExpiry, maskCvv, maskCpf, detectBrand } from "../../Utils/cardFormat";

const RELOAD_AFTER_MS = 60 * 60 * 1000; // 1h

// Nome da agência: usa o nome fantasia; se vazio ou "Não definido", cai na razão
// social (quando o backend a enviar em agenciaRazaoSocial).
const NAO_DEF = /^n[ãa]o\s*definid[oa]$/i;
const resolveAgencyName = (l) => {
	if (!l) return null;
	const fantasia = (l.agencia || "").trim();
	if (fantasia && !NAO_DEF.test(fantasia)) return fantasia;
	return (l.agenciaRazaoSocial || "").trim() || fantasia || null;
};
// CPF do responsável (quando o backend enviar; usado p/ prefill quando há restrição).
const responsavelCpfOf = (l) => (l && (l.responsavelCpf || l.pagador?.cpf)) || "";

// O backend já retorna `errorMessage` em PT (mapeado do ReturnCode da Cielo) —
// exibimos direto. Fallback só quando vier vazio.
const friendlyPayError = (code, raw) => {
	const r = String(raw || "").trim();
	if (r) return r;
	if (code === "PAYMENT_DECLINED") return "Pagamento recusado pela operadora do cartão. Verifique os dados ou tente outro cartão.";
	return "Não foi possível concluir o pagamento. Tente novamente.";
};

// Qual campo realçar a partir do code do erro (INVALID_CARD não distingue número/CVV,
// então olhamos a mensagem; INVALID_CUSTOMER/CPF_MISMATCH → CPF).
const errorFieldOf = (code, msg) => {
	if (code === "INVALID_CUSTOMER" || code === "CPF_MISMATCH") return "cpf";
	if (code === "INVALID_CARD") return /cvv|seguran/i.test(String(msg || "")) ? "cvv" : "number";
	return null;
};

// Wrapper visual em escopo de MÓDULO (estável — evita remontar inputs / perder foco).
const Shell = ({ agencyLogo, agencia, children }) => (
	<div style={{ minHeight: "100vh", background: "#F2F4F7", padding: "1.5rem", display: "flex", justifyContent: "center" }}>
		<div style={{ width: "min(720px, 100%)" }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
				<img src={dream4Logo} alt="Dream4" style={{ height: "34px" }} />
				<div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
					{agencyLogo && <img src={agencyLogo} alt="Agência" style={{ maxHeight: "44px", maxWidth: "150px", objectFit: "contain" }} />}
					{agencia && <span style={{ fontWeight: 600, color: "#2E0249" }}>{agencia}</span>}
				</div>
			</div>
			<div style={{ background: "#fff", borderRadius: "16px", padding: "1.5rem", boxShadow: "0 2px 14px rgba(0,0,0,0.06)" }}>
				{children}
			</div>
			<SecurityFooter />
		</div>
	</div>
);

// Rodapé + modal de segurança (#7)
const SecurityFooter = () => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<div style={{ textAlign: "center", marginTop: "1rem" }}>
				<Button label="Saiba porque o pagamento é 100% seguro" icon="pi pi-shield" text size="small" onClick={() => setOpen(true)} style={{ color: "#6B6B6B" }} />
			</div>
			<Dialog header="Segurança do pagamento" visible={open} onHide={() => setOpen(false)} style={{ width: "min(520px, 95vw)" }} dismissableMask>
				<div style={{ fontSize: "0.9rem", color: "#1D2939", lineHeight: 1.6 }}>
					<p>Seu pagamento é processado pela <strong>Cielo</strong>, uma das maiores adquirentes do Brasil, em ambiente <strong>certificado PCI-DSS</strong>.</p>
					<ul style={{ paddingLeft: "1.2rem" }}>
						<li>Conexão criptografada com <strong>certificado SSL</strong>.</li>
						<li>Os dados do seu cartão são enviados diretamente ao processador — a Dream4 e a agência <strong>não armazenam</strong> o número do cartão.</li>
						<li>Antifraude e autenticação do emissor do cartão.</li>
					</ul>
					<p style={{ color: "#6B6B6B" }}>Em caso de dúvidas, fale com a sua agência.</p>
				</div>
			</Dialog>
		</>
	);
};

/**
 * PaymentLinkPublic (/pagamento/:token) — página PÚBLICA (sem login), fora do
 * layout (ver App.js). Fase 1: pagamento SIMULADO (o real usa o backend Cielo).
 */
const PaymentLinkPublic = (props) => {
	const { token } = useParams();
	const { selectedLink, exchangeRates } = props;
	const { openPaymentLink, checkPaymentLinkStatus, payPaymentLink, retrieveExchangeRate, setExchangeRates } = props;

	const [loading, setLoading] = useState(true);
	const [paying, setPaying] = useState(false);
	const [card, setCard] = useState({ number: "", name: "", expiry: "", cvv: "", cpf: "" });
	const [installments, setInstallments] = useState(1);
	const [payErrorField, setPayErrorField] = useState(null); // realça o campo do erro
	// Atualiza o cartão e limpa o realce de erro ao digitar.
	const updateCard = (patch) => { setCard((c) => ({ ...c, ...patch })); setPayErrorField(null); };
	const [pdfOpen, setPdfOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [pdfBusy, setPdfBusy] = useState(false);
	const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1023px)" });
	const loadedAtRef = useRef(0);
	const toast = useRef(null);
	const statusRef = useRef(null);
	statusRef.current = selectedLink?.status || null; // status atual p/ comparar no re-check
	// Logo vem do próprio link público (agenciaLogoUrl), prefixada com o host.
	const agencyLogo = selectedLink ? absoluteLogoUrl(selectedLink.agenciaLogoUrl) : null;

	// Prefill do CPF do titular quando há restrição (titular = responsável travado).
	useEffect(() => {
		const rcpf = responsavelCpfOf(selectedLink);
		if (selectedLink && selectedLink.aplicaRestricao && rcpf) {
			setCard((c) => ({ ...c, cpf: maskCpf(rcpf) }));
		}
	}, [selectedLink]);

	const loadData = React.useCallback(async () => {
		try {
			const res = await retrieveExchangeRate();
			if (res && res.success && res.exchangeRates) setExchangeRates(res.exchangeRates);
		} catch (e) { /* segue sem câmbio fresco */ }
		try { await openPaymentLink(token); } catch (e) { /* link inválido -> selectedLink null */ }
		loadedAtRef.current = Date.now();
		setLoading(false);
	}, [token, retrieveExchangeRate, setExchangeRates, openPaymentLink]);

	useEffect(() => { loadData(); }, [loadData]);

	// Ao voltar à aba: sempre re-checa status; se faz mais de 1h, recarrega tudo (#5).
	useEffect(() => {
		const onVis = () => {
			if (document.visibilityState !== "visible") return;
			if (Date.now() - loadedAtRef.current > RELOAD_AFTER_MS) { loadData(); return; }
			// re-check leve; se o status mudou (pago/expirado/cancelado em outro fluxo),
			// re-abre p/ refletir o estado real na página.
			checkPaymentLinkStatus(token)
				.then((st) => { if (st && st !== statusRef.current) openPaymentLink(token); })
				.catch(() => {});
		};
		document.addEventListener("visibilitychange", onVis);
		return () => document.removeEventListener("visibilitychange", onVis);
	}, [token, loadData, checkPaymentLinkStatus, openPaymentLink]);

	// Comprovante em PDF (server-side, como a cotação): GET .../comprovante.
	const handleDownloadReceipt = () => {
		setPdfBusy(true);
		downloadReceiptPdf(token)
			.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao gerar o PDF", detail: "Não foi possível gerar o comprovante agora. Tente novamente.", life: 5000 }))
			.finally(() => setPdfBusy(false));
	};
	const handleViewReceipt = () => {
		setPdfBusy(true);
		if (isTabletOrMobile) {
			viewReceiptPdf(token)
				.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao abrir o PDF", detail: "Não foi possível abrir o comprovante agora. Tente novamente.", life: 5000 }))
				.finally(() => setPdfBusy(false));
			return;
		}
		fetchReceiptPdfUrl(token)
			.then((url) => { setPdfUrl(url); setPdfOpen(true); })
			.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao abrir o PDF", detail: "Não foi possível abrir o comprovante agora. Tente novamente.", life: 5000 }))
			.finally(() => setPdfBusy(false));
	};
	const closeReceiptViewer = () => { setPdfOpen(false); setPdfUrl((u) => { if (u) URL.revokeObjectURL(u); return null; }); };

	const agencia = resolveAgencyName(selectedLink);

	if (loading) {
		return <Shell agencyLogo={agencyLogo} agencia={agencia}><div style={{ textAlign: "center", padding: "2rem" }}><ProgressSpinner /></div></Shell>;
	}
	if (!selectedLink) {
		return <Shell agencyLogo={agencyLogo} agencia={agencia}><div style={{ textAlign: "center", padding: "1.5rem" }}><i className="pi pi-exclamation-triangle" style={{ fontSize: "2rem", color: "#c0392b" }} /><h2>Link inválido</h2><p style={{ color: "#6B6B6B" }}>Este link de pagamento não foi encontrado.</p></div></Shell>;
	}

	const link = selectedLink;
	const snap = link.snapshotReserva || {};
	const reservas = snap.reservas || [];
	// O link público pode não trazer reservationIds — deriva do snapshot.
	const reservationIds = link.reservationIds || reservas.map((r) => r.id).filter(Boolean);
	// Link público de visualização do hotel (nova aba): /hotel/:hotelViewToken?a=:agencyViewToken.
	const hotelViewUrl = (r) => {
		const t = r && r.hotelViewToken;
		if (!t) return null;
		return `${publicBaseUrl()}/hotel/${t}${link.agencyViewToken ? `?a=${encodeURIComponent(link.agencyViewToken)}` : ""}`;
	};
	const snapTotal = snap.valorTotalUSD;
	const totalOriginalUSD = link.valorOriginalUSD || ((snapTotal !== null && snapTotal !== undefined) ? snapTotal : reservas.reduce((s, r) => s + (r.valorUSD || 0), 0));
	// valor a cobrar = com abate da comissão antecipada (se houver); senão, o total
	const valorUSD = (link.valorCobrarUSD && link.valorCobrarUSD > 0) ? link.valorCobrarUSD : totalOriginalUSD;
	const rate = getUsdRate(exchangeRates);
	const usdBrl = (rate !== null && rate !== undefined) ? rate : (link.cambioNoOpen ? link.cambioNoOpen.usdBrl : null);
	const valorBRL = (usdBrl !== null && usdBrl !== undefined) ? valorUSD * usdBrl : null;
	const toBRL = (usd) => (usdBrl !== null && usdBrl !== undefined ? usd * usdBrl : null);
	const urgency = getDeadlineUrgency(link.cancellationDeadline);

	// Estado: pago (#9 + #10)
	if (link.status === PAYMENT_LINK_STATUS.PAGO) {
		const tx = link.transacao || {};
		return (
			<Shell agencyLogo={agencyLogo} agencia={agencia}>
				<Toast ref={toast} position="bottom-center" />
				<div style={{ textAlign: "center", padding: "0.5rem 0 1rem" }}>
					<i className="pi pi-check-circle" style={{ fontSize: "2.5rem", color: "#2e9e5b" }} />
					<h2 style={{ color: "#2E0249", marginBottom: "0.25rem" }}>Pagamento confirmado!</h2>
					<p style={{ color: "#6B6B6B", marginTop: 0 }}>
						A sua agência e você receberão um e-mail com a confirmação do pagamento desta reserva.
						Em caso de dúvidas, entre em contato com a sua agência.
					</p>
				</div>
				<div style={{ background: "#F2F4F7", borderRadius: "12px", padding: "1rem", fontSize: "0.85rem", color: "#1D2939" }}>
					<div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Comprovante</div>
					<Row label="Código da transação" value={tx.transactionId || tx.codigo || "—"} />
					{tx.merchantOrderId ? <Row label="Pedido" value={tx.merchantOrderId} /> : null}
					<Row label="Adquirente" value={tx.gateway || tx.adquirente || "Cielo"} />
					{tx.installments ? <Row label="Parcelas" value={`${tx.installments}x`} /> : null}
					<Row label="Pago em" value={(link.pagoEm || tx.data) ? moment(link.pagoEm || tx.data).format("DD/MM/YYYY HH:mm") : "—"} />
					<Row label="Valor" value={tx.amount != null ? formatNumberCurrency(tx.amount, tx.currency || "BRL") : (valorBRL !== null ? formatNumberCurrency(valorBRL, "BRL") : formatNumberCurrency(valorUSD, "USD"))} />
						<div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "0.75rem", flexWrap: "wrap" }}>
							<Button label="Visualizar PDF" icon="pi pi-eye" outlined onClick={handleViewReceipt} loading={pdfBusy} disabled={pdfBusy} />
							<Button label="Baixar PDF" icon="pi pi-download" outlined onClick={handleDownloadReceipt} loading={pdfBusy} disabled={pdfBusy} />
						</div>
				</div>
				{/* Visualizador de PDF em modal (desktop) — iframe nativo */}
				<Dialog header="Comprovante de pagamento" visible={pdfOpen} onHide={closeReceiptViewer} style={{ width: "min(900px, 96vw)", height: "90vh" }} maximizable dismissableMask>
					{pdfUrl ? (
						<iframe title="Comprovante" src={pdfUrl} style={{ width: "100%", height: "100%", border: "none" }} />
					) : null}
				</Dialog>
			</Shell>
		);
	}

	// Estado: expirado/vencido
	if (link.status === PAYMENT_LINK_STATUS.EXPIRADO || urgency.level === "vencido") {
		return (
			<Shell agencyLogo={agencyLogo} agencia={agencia}>
				<div style={{ textAlign: "center", padding: "1.5rem" }}>
					<i className="pi pi-clock" style={{ fontSize: "2.5rem", color: "#c0392b" }} />
					<h2 style={{ color: "#2E0249" }}>Link expirado</h2>
					<p style={{ color: "#6B6B6B" }}>O prazo para pagamento desta reserva venceu. Entre em contato com a agência para mais informações.</p>
				</div>
			</Shell>
		);
	}

	const deadlineBanner = () => {
		if (!link.cancellationDeadline) return null;
		const map = {
			normal: { bg: "#EEF6FF", border: "#BBD6FF", color: "#1D2939", icon: "pi-info-circle" },
			aviso: { bg: "#FFF8E1", border: "#FFE082", color: "#7A5C00", icon: "pi-exclamation-triangle" },
			urgente: { bg: "#FDECEA", border: "#F5B7B1", color: "#922B21", icon: "pi-exclamation-circle" }
		};
		const s = map[urgency.level] || map.normal;
		const txt = urgency.level === "urgente"
			? `Atenção: faltam menos de 24h para o vencimento (${moment(link.cancellationDeadline).format("DD/MM/YYYY")}). Pague o quanto antes para não perder a reserva.`
			: urgency.level === "aviso"
				? `A data limite para pagamento é ${moment(link.cancellationDeadline).format("DD/MM/YYYY")} (faltam ${urgency.diasRestantes} dia(s)).`
				: `Data limite para pagamento: ${moment(link.cancellationDeadline).format("DD/MM/YYYY")}.`;
		return (
			<div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", background: s.bg, border: `1px solid ${s.border}`, borderRadius: "8px", padding: "0.6rem 0.8rem", marginBottom: "1rem", fontSize: "0.82rem", color: s.color }}>
				<i className={`pi ${s.icon}`} style={{ marginTop: "2px" }} />
				<span>{txt}</span>
			</div>
		);
	};

	const brand = detectBrand(card.number);
	const lblStyle = { display: "block", textAlign: "left", fontSize: "0.8rem", fontWeight: 600, color: "#2E0249", marginBottom: "0.25rem" };
	const cpfLocked = !!(link.aplicaRestricao && responsavelCpfOf(link));
	const cpfObrigatorio = !link.responsavelEstrangeiro;
	// Parcelamento: mínimo de US$ 25 por parcela, até 12x sem juros.
	const minParcela = 25;
	const maxParcelas = Math.max(1, Math.min(12, Math.floor((valorUSD || 0) / minParcela)));
	const installmentsOptions = Array.from({ length: maxParcelas }, (_, i) => {
		const num = i + 1;
		const perUSD = (valorUSD || 0) / num;
		const perBRL = toBRL(perUSD);
		return { value: num, label: `${num}x de ${perBRL !== null ? formatNumberCurrency(perBRL, "BRL") : formatNumberCurrency(perUSD, "USD")} sem juros` };
	});
	const podePagar = onlyDigits(card.number).length >= 13 && card.name.trim() && card.expiry.length === 5 && onlyDigits(card.cvv).length >= 3 && (!cpfObrigatorio || onlyDigits(card.cpf).length === 11) && installments >= 1;

	const handlePay = () => {
		setPayErrorField(null);
		if (!validarNumeroCartao(card.number)) {
			setPayErrorField("number");
			toast.current?.show({ severity: "warn", summary: "Cartão inválido", detail: "Confira o número do cartão e tente novamente.", life: 5000 });
			return;
		}
		if (!validarValidadeCartao(card.expiry)) {
			setPayErrorField("expiry");
			toast.current?.show({ severity: "warn", summary: "Validade inválida", detail: "A validade do cartão está incorreta ou vencida (MM/AA).", life: 5000 });
			return;
		}
		setPaying(true);
		const body = {
			creditCard: {
				cardNumber: onlyDigits(card.number),
				holder: card.name.trim(),
				expirationDate: card.expiry,
				securityCode: onlyDigits(card.cvv),
				brand: (brand || "").toLowerCase(),
			},
			installments,
			// Opção A: o backend deriva nome/e-mail do pagador (link/responsável).
			// O FE envia só o CPF do titular do cartão (campo) para R4/adquirente.
			customer: { identity: onlyDigits(card.cpf) },
		};
		payPaymentLink(token, body)
			.then((data) => {
				if (data && data.pending) {
					toast.current?.show({ severity: "warn", summary: "Pagamento em processamento", detail: "Estamos confirmando o pagamento — você receberá a confirmação por e-mail.", life: 7000 });
					checkPaymentLinkStatus(token);
				} else {
					toast.current?.show({ severity: "success", summary: "Pagamento aprovado!", life: 4000 });
					// o thunk já atualizou o link para 'pago' (renderiza o comprovante)
				}
			})
			.catch((e) => {
				const code = e?.response?.data?.code;
				const msg = e?.response?.data?.errorMessage;
				if (["ALREADY_PAID", "EXPIRED", "CANCELLED", "NOT_PAYABLE"].includes(code)) {
					toast.current?.show({ severity: "info", summary: "Status atualizado", detail: msg || "Este link não está mais disponível para pagamento.", life: 6000 });
					openPaymentLink(token); // re-busca o estado real (pago/expirado/cancelado)
				} else if (code === "CPF_MISMATCH") {
					setPayErrorField("cpf");
					toast.current?.show({ severity: "error", summary: "CPF não confere", detail: msg || "O CPF do titular do cartão deve ser o mesmo do responsável pela reserva.", life: 7000 });
				} else {
					setPayErrorField(errorFieldOf(code, msg));
					toast.current?.show({ severity: "error", summary: "Pagamento não concluído", detail: friendlyPayError(code, msg), life: 7000 });
				}
			})
			.finally(() => setPaying(false));
	};

	return (
		<Shell agencyLogo={agencyLogo} agencia={agencia}>
			<Toast ref={toast} position="bottom-center" />
				{deadlineBanner()}

			<h2 style={{ color: "#2E0249", marginTop: 0, marginBottom: "0.25rem" }}>{reservationIds.length > 1 ? "Pagamento de suas reservas" : "Pagamento da sua reserva"}</h2>
			{/* Texto de referência (#8) */}
			<p style={{ color: "#6B6B6B", marginTop: 0, fontSize: "0.9rem", lineHeight: 1.5 }}>
				Este pagamento é referente {reservationIds.length > 1 ? "às reservas" : "à reserva"}{" "}
				<strong>{reservationIds.map((id) => `#${id}`).join(", ") || "—"}</strong>
				{agencia ? <> {reservationIds.length > 1 ? "feitas" : "feita"} pela agência <strong>{agencia}</strong></> : null}
				{snap.reservaData ? `, em ${moment(snap.reservaData).format("DD/MM/YYYY [às] HH:mm")}` : ""}
				{snap.agente ? `, pelo agente ${snap.agente}` : ""}.
				<br />Confira os dados abaixo e realize o pagamento. O valor em reais é válido para hoje e pode mudar conforme a cotação do dia.
			</p>

			{/* Pagador (#1) */}
			<div style={{ border: "1px solid #e3e3e3", borderRadius: "12px", padding: "0.85rem", marginBottom: "1rem" }}>
				<div style={{ fontWeight: 600, color: "#2E0249", marginBottom: "0.35rem" }}>Responsável pelo Pagamento</div>
				<div style={{ fontSize: "0.86rem", color: "#1D2939" }}>{link.pagador?.nome || "—"}</div>
				<div style={{ fontSize: "0.82rem", color: "#6B6B6B" }}>{link.pagador?.email || "—"}</div>
					{link.pagador?.telefone ? <div style={{ fontSize: "0.82rem", color: "#6B6B6B" }}>{link.pagador.telefone}</div> : null}
			</div>

			{/* Reservas (#2) */}
			{reservas.map((r, i) => {
				const itemBRL = toBRL(r.valorUSD || 0);
				return (
					<div key={i} style={{ display: "flex", gap: "0.75rem", border: "1px solid #e3e3e3", borderRadius: "12px", padding: "0.75rem", marginBottom: "0.75rem" }}>
						<img src={(r.hotel && r.hotel.foto) || imagePlaceHolder} onError={(e) => { e.target.src = imagePlaceHolder; }} alt="" style={{ width: "120px", height: "90px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }} />
						<div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
							<div style={{ fontWeight: 700 }}>
								{hotelViewUrl(r) ? (
									<a href={hotelViewUrl(r)} target="_blank" rel="noopener noreferrer" style={{ color: "#814DE5", textDecoration: "none" }} title="Ver detalhes do hotel">
										{(r.hotel && r.hotel.nome) || "Hotel"} <i className="pi pi-external-link" style={{ fontSize: "0.7rem" }} />
									</a>
								) : (
									<span style={{ color: "#2E0249" }}>{(r.hotel && r.hotel.nome) || "Hotel"}</span>
								)}
							</div>
							<div style={{ fontSize: "0.8rem", color: "#6B6B6B" }}>{[r.hotel && r.hotel.cidade, r.hotel && r.hotel.pais].filter(Boolean).join(", ")}</div>
							{r.periodo?.checkIn && (
								<div style={{ fontSize: "0.8rem", color: "#6B6B6B" }}>{moment(r.periodo.checkIn).format("DD/MM/YYYY")} – {moment(r.periodo.checkOut).format("DD/MM/YYYY")}</div>
							)}
							{r.quarto?.nome && <div style={{ fontSize: "0.8rem", color: "#6B6B6B" }}>{r.quarto.nome}</div>}
							{r.quarto?.cafe ? <div style={{ fontSize: "0.78rem", color: "#2e9e5b" }}><i className="pi pi-check" style={{ fontSize: "0.7rem" }} /> {r.quarto.cafe}</div> : null}
							<div style={{ marginTop: "0.35rem", fontSize: "0.85rem" }}>
								<strong style={{ color: "#2E0249" }}>{itemBRL !== null ? formatNumberCurrency(itemBRL, "BRL") : formatNumberCurrency(r.valorUSD || 0, "USD")}</strong>
								<span style={{ color: "#888" }}> · {formatNumberCurrency(r.valorUSD || 0, "USD")}</span>
							</div>
						</div>
					</div>
				);
			})}

				{link.aplicaRestricao && (
					<div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", background: "#FDECEA", border: "1px solid #F5B7B1", borderRadius: "8px", padding: "0.6rem 0.8rem", marginBottom: "1rem", fontSize: "0.8rem", color: "#922B21" }}>
						<i className="pi pi-lock" style={{ marginTop: "2px" }} />
						<span>Por segurança, o pagamento desta reserva só pode ser feito pelo <strong>responsável</strong> (o CPF do titular do cartão deve ser o mesmo).</span>
					</div>
				)}

				{/* Total + conversão + warning */}
			<div style={{ background: "#F2F4F7", borderRadius: "12px", padding: "1rem", marginBottom: "1rem" }}>
				{link.advanceComission && (
					<>
						<div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: "#1D2939" }}>
							<span style={{ color: "#6B6B6B" }}>Subtotal</span>
							<span>{toBRL(totalOriginalUSD) !== null ? formatNumberCurrency(toBRL(totalOriginalUSD), "BRL") : formatNumberCurrency(totalOriginalUSD, "USD")}</span>
						</div>
						<div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: "#c0392b", paddingBottom: "0.4rem", borderBottom: "1px solid #ddd", marginBottom: "0.4rem" }}>
							<span>Pagamento de entrada</span>
							<span>− {toBRL(link.comissaoUSD || 0) !== null ? formatNumberCurrency(toBRL(link.comissaoUSD || 0), "BRL") : formatNumberCurrency(link.comissaoUSD || 0, "USD")}</span>
						</div>
					</>
				)}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
					<span style={{ color: "#6B6B6B" }}>Total a pagar</span>
					<span style={{ fontSize: "1.6rem", fontWeight: 800, color: "#2E0249" }}>{valorBRL !== null ? formatNumberCurrency(valorBRL, "BRL") : formatNumberCurrency(valorUSD, "USD")}</span>
				</div>
				<div style={{ textAlign: "right", color: "#6B6B6B", fontSize: "0.8rem" }}>{formatNumberCurrency(valorUSD, "USD")}{usdBrl ? ` · US$ 1 = ${formatNumberCurrency(usdBrl, "BRL")}` : ""}</div>
				<div style={{ display: "flex", gap: "0.4rem", alignItems: "flex-start", marginTop: "0.6rem", fontSize: "0.76rem", color: "#7A5C00", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "8px", padding: "0.5rem 0.7rem" }}>
					<i className="pi pi-exclamation-triangle" style={{ marginTop: "1px" }} />
					<span>O valor em reais é calculado pela cotação do dólar deste instante e pode mudar até a confirmação do pagamento.</span>
				</div>
			</div>

			{/* Pagamento */}
			<div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
				<Button label="Cartão de crédito" icon="pi pi-credit-card" className="w100" />
				<Button label="PIX (em breve)" icon="pi pi-qrcode" outlined disabled className="w100" />
			</div>

			<div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
				{/* Linha 1: Número + Validade + CVV */}
				<div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "flex-start" }}>
					<div style={{ flex: "2 1 14rem" }}>
						<label style={lblStyle}>Número do cartão</label>
						<span className="p-input-icon-right" style={{ width: "100%" }}>
							{brand && <i style={{ fontStyle: "normal", fontWeight: 700, color: "#814DE5", fontSize: "0.78rem" }}>{brand}</i>}
							<InputText value={card.number} onChange={(e) => updateCard({ number: maskCard(e.target.value) })} placeholder="0000 0000 0000 0000" style={{ width: "100%" }} inputMode="numeric" className={payErrorField === "number" ? "p-invalid" : undefined} />
						</span>
					</div>
					<div style={{ flex: "1 1 8rem" }}>
						<label style={lblStyle}>Validade</label>
						<InputText value={card.expiry} onChange={(e) => updateCard({ expiry: maskExpiry(e.target.value) })} placeholder="MM/AA" className={"w100" + (payErrorField === "expiry" ? " p-invalid" : "")} inputMode="numeric" />
					</div>
					<div style={{ flex: "0 1 7rem" }}>
						<label style={lblStyle}>CVV</label>
						<InputText value={card.cvv} onChange={(e) => updateCard({ cvv: maskCvv(e.target.value) })} placeholder="000" className={"w100" + (payErrorField === "cvv" ? " p-invalid" : "")} inputMode="numeric" />
					</div>
				</div>
				{/* Linha 2: Nome impresso + CPF do titular */}
				<div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "flex-start" }}>
					<div style={{ flex: "1 1 12rem" }}>
						<label style={lblStyle}>Nome impresso no cartão</label>
						<InputText value={card.name} onChange={(e) => updateCard({ name: e.target.value })} placeholder="Como está no cartão" className="w100" />
					</div>
					{cpfObrigatorio && (
						<div style={{ flex: "1 1 12rem" }}>
							<label style={lblStyle}>CPF do titular do cartão</label>
							<InputText value={card.cpf} onChange={(e) => updateCard({ cpf: maskCpf(e.target.value) })} placeholder="000.000.000-00" inputMode="numeric" readOnly={cpfLocked} className={"w100" + (payErrorField === "cpf" ? " p-invalid" : "")} style={cpfLocked ? { background: "#f4f4f4" } : {}} />
						</div>
					)}
				</div>
				{/* Mensagem do CPF — linha inteira abaixo */}
				{cpfObrigatorio && (
					<div style={{ fontSize: "0.74rem", color: "#6B6B6B", display: "flex", gap: "0.4rem", alignItems: "flex-start" }}>
						<i className="pi pi-info-circle" style={{ marginTop: "1px" }} />
						<span>{cpfLocked
							? "Preenchimento automático a partir do responsável pela reserva — somente o titular responsável pode pagar."
							: "Por segurança, o titular do cartão deve ser a mesma pessoa responsável pela reserva (mesmo CPF), salvo se a agência tiver liberado essa restrição. Verificado no momento do pagamento."}</span>
					</div>
				)}
				{/* Linha 3: Parcelamento */}
				<div>
					<label style={lblStyle}>Parcelamento</label>
					<Dropdown value={installments} options={installmentsOptions} optionLabel="label" optionValue="value" onChange={(e) => setInstallments(e.value)} placeholder="Selecione..." className="w100" style={{ textAlign: "left" }} itemTemplate={(o) => <div style={{ textAlign: "left" }}>{o.label}</div>} valueTemplate={(o, props2) => <div style={{ textAlign: "left" }}>{o ? o.label : props2.placeholder}</div>} />
				</div>
			</div>

			<Button
				label={paying ? "Processando..." : `Pagar ${valorBRL !== null ? formatNumberCurrency(valorBRL, "BRL") : formatNumberCurrency(valorUSD, "USD")}`}
				icon="pi pi-lock"
				className="w100"
				style={{ marginTop: "1rem", background: "#814DE5", borderColor: "#814DE5" }}
				loading={paying}
				disabled={!podePagar || paying}
				onClick={handlePay}
			/>
			<div style={{ textAlign: "center", color: "#9aa0a6", fontSize: "0.72rem", marginTop: "0.5rem" }}>
				Pagamento processado com segurança pela Cielo (ambiente PCI-DSS).
			</div>
		</Shell>
	);
};

// Linha de comprovante
const Row = ({ label, value }) => (
	<div style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0", borderTop: "1px dashed #e3e3e3" }}>
		<span style={{ color: "#6B6B6B" }}>{label}</span>
		<strong>{value}</strong>
	</div>
);

const mapStateToProps = (store) => ({
	selectedLink: store.paymentLinkReducer.selectedLink,
	exchangeRates: store.orderReducer.exchangeRates
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...PaymentLinkActions, ...OrderActions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(PaymentLinkPublic);
